import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFeatureGate } from '../../hooks/useFeatureGate'
import { useRole } from '../../hooks/auth/useRole'
import UnderlineTabBar from '../tables/base/UnderlineTabBar'

const LAST_HIRING_ROUTE = 'aboutwrk:people-flow:hiring-route'
const LAST_DEPARTURE_ROUTE = 'aboutwrk:people-flow:departure-route'

const safeStoredRoute = (key: string, prefix: string, fallback: string) => {
  if (typeof window === 'undefined') return fallback
  const stored = window.sessionStorage.getItem(key)
  if (stored && (stored === prefix || stored.startsWith(`${prefix}/`))) return stored
  return fallback
}

/**
 * A human-readable switch between the two halves of the employee lifecycle.
 * Existing inflow/outflow URLs remain canonical so bookmarks, emails, and API
 * cache keys keep working; this component only unifies how people navigate them.
 */
const PeopleFlowSwitcher: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { isOrgAdmin, isSuperAdmin } = useRole()
  const { hasFeature } = useFeatureGate()

  const isHiring = location.pathname.startsWith('/user/inflow')
  const isDeparture = location.pathname.startsWith('/user/outflow')
  const canSeeHiring = isSuperAdmin || hasFeature('inflow') || isHiring
  const canSeeDepartures =
    (isOrgAdmin || isSuperAdmin) && (isSuperAdmin || hasFeature('outflow') || isDeparture)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const route = `${location.pathname}${location.search}${location.hash}`
    if (isHiring) window.sessionStorage.setItem(LAST_HIRING_ROUTE, route)
    if (isDeparture) window.sessionStorage.setItem(LAST_DEPARTURE_ROUTE, route)
  }, [isDeparture, isHiring, location.hash, location.pathname, location.search])

  const choices = [
    ...(canSeeHiring
      ? [{
          id: 'hiring',
          label: t('peopleFlow.hiring.title', 'Hiring & onboarding'),
          active: isHiring,
          path: safeStoredRoute(LAST_HIRING_ROUTE, '/user/inflow', '/user/inflow/candidates'),
        }]
      : []),
    ...(canSeeDepartures
      ? [{
          id: 'departures',
          label: t('peopleFlow.departures.title', 'Departures'),
          active: isDeparture,
          path: safeStoredRoute(LAST_DEPARTURE_ROUTE, '/user/outflow', '/user/outflow/offboarding'),
        }]
      : []),
  ]

  if (choices.length < 2) return null

  return (
    <UnderlineTabBar
      tabs={choices.map((choice) => ({ value: choice.id, label: choice.label }))}
      selectedTab={isHiring ? 'hiring' : 'departures'}
      onSelectTab={(choiceId) => {
        const choice = choices.find(({ id }) => id === choiceId)
        if (choice && !choice.active) navigate(choice.path)
      }}
    />
  )
}

export default PeopleFlowSwitcher
